/**
 * rendering-registry/index.ts
 *
 * Public API for the TeachBud Rendering Registry.
 *
 * USAGE
 * ─────────────────────────────────────────────────────────────────────────────
 * import { resolveForPrompt, resolveForHint, getWsHeight, REGISTRY_VERSION }
 *   from './rendering-registry';
 *
 * ADDING A NEW REPRESENTATION
 * ─────────────────────────────────────────────────────────────────────────────
 * See docs/adding-a-representation.md for the full 6-step process.
 *
 * STARTUP VALIDATION
 * ─────────────────────────────────────────────────────────────────────────────
 * validateRegistry() runs once when this module is first imported.
 * It throws RegistryConflictError if two entries have identical match criteria
 * or if any fallback chain has a cycle. Fail early, fail loudly.
 */

// Re-export everything generators need
export { REGISTRY_VERSION, REGISTRY, WS_HEIGHTS_BY_KIND } from './registry';
// Grade-band layout system (Spec §2)
export { getGradeBandConfig, getGradeBandOverrides, gradeNumFromLabel } from './grade-band';
export type { GradeBandConfig, GradeBandLayoutOverrides } from './grade-band';
export { resolveForPrompt, resolveForHint, resolveWithFallback, getWsHeight, validateRegistry, checkBlockedResolutions } from './resolver';
export type { BlockedProblemInfo, ProblemRef } from './resolver';
export type {
  WorkspaceKind,
  WorkspaceSize,
  RepresentationType,
  CognitiveOp,
  ConceptDomain,
  RegistryEntry,
  ResolutionContext,
  Resolution,
  ResolvedRepresentation,
  BlockedResolution,
  FallbackBehavior,
  MatchCriteria,
} from './types';
export { RegistryConflictError } from './types';

// ── Startup conflict check ─────────────────────────────────────────────────────
// Imported at module init so any registry conflict is surfaced immediately,
// not on the first lesson generation. In development this throws; in production
// it logs and continues so a mis-configured entry never brings down the whole app.

import { validateRegistry } from './resolver';

try {
  validateRegistry();
} catch (err) {
  if (import.meta.env?.DEV) {
    throw err; // Hard fail in development — fix the registry before continuing
  } else {
    console.error('[TeachBud] Rendering registry conflict detected:', err);
  }
}
